import React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/domains/auth/useAuth";
import { useNotificationSettings } from "@/hooks/settings";
import { AlertCircle, Bell, Loader2, Mail } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";

interface NotificationSettingsControlsProps {
  isLoading?: boolean;
  error?: boolean;
  onRetry?: () => void;
}

const NotificationSettingsControls: React.FC<
  NotificationSettingsControlsProps
> = ({ error: propsError, onRetry }) => {
  const { user } = useAuth();

  // Use our new hook instead of direct Supabase calls
  const { settings, toggles, isLoading, isSubmitting, isError, error, retry } =
    useNotificationSettings(user!);

  // Show error alert if there's an error
  if (isError || propsError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Notification Settings</CardTitle>
          <CardDescription>
            Manage how you receive notifications
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error instanceof Error
                ? error.message
                : "Failed to load notification settings"}
            </AlertDescription>
          </Alert>
          <div className="mt-4 flex justify-end">
            <Button variant="outline" onClick={onRetry || retry}>
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Notification Settings</CardTitle>
          <CardDescription>
            Loading your notification preferences...
          </CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          Notification Settings
        </CardTitle>
        <CardDescription>
          Manage how you receive notifications about your profile activity
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* In-app notifications */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium">In-App Notifications</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Control which in-app notifications you receive when users interact
            with your profile
          </p>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="notify-views">Profile Views</Label>
              <p className="text-sm text-muted-foreground">
                Get notified when someone views your profile
              </p>
            </div>
            <Switch
              id="notify-views"
              checked={settings?.notify_views}
              onCheckedChange={toggles.notifyViews}
              disabled={isSubmitting}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="notify-likes">Profile Likes</Label>
              <p className="text-sm text-muted-foreground">
                Get notified when someone likes your profile
              </p>
            </div>
            <Switch
              id="notify-likes"
              checked={settings?.notify_likes}
              onCheckedChange={toggles.notifyLikes}
              disabled={isSubmitting}
            />
          </div>
        </div>

        <Separator />

        {/* Email notifications */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium">Email Notifications</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Control how frequently you receive email notifications
          </p>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="email-realtime">Real-time Alerts</Label>
              <p className="text-sm text-muted-foreground">
                Get immediate email notifications for important activity
              </p>
            </div>
            <Switch
              id="email-realtime"
              checked={settings?.email_realtime}
              onCheckedChange={toggles.emailRealtime}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default React.memo(NotificationSettingsControls);
